/*
 * CAPTURE-OWNED MODULE
 * Processing Monitor — real-time visibility into document processing pipeline.
 * Shows upload → OCR → extraction → routing status for all documents.
 * Supports filtering by batch, status, and provides retry actions.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureAPI, handleAPIError } from '../services/api';
import { reclassifyDocument } from '../api/captureExtraction';
import { formatDocType } from '../utils/formatDocType';
import { describeBulkDeleteFailures } from '../utils/bulkDeleteFailures';
import { useIsAdmin } from '../identity/useIsAdmin';
import {
  MIN_COLUMN_WIDTH,
  normalizeColumnPrefs,
  mergeColumnWidths,
  clearColumnWidth,
  hasAnyColumnWidth,
} from '../utils/monitorColumnPrefs';
import './ProcessingMonitorPage.css';

// A document is considered stuck if it has been in an active processing
// state for longer than this threshold with no progress.
const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

function isStuck(doc) {
  if (!['processing_extraction', 'processing_ocr', 'ocr_complete'].includes(doc.status)) return false;
  const updatedAt = doc.updatedAt || doc.createdAt;
  if (!updatedAt) return false;
  return (Date.now() - new Date(updatedAt).getTime()) > STUCK_THRESHOLD_MS;
}

// ── Status configuration ──

const STATUS_CONFIG = {
  pending_ocr:            { label: 'Pending OCR',       color: 'gray',   icon: '⏳', phase: 'ocr' },
  processing_ocr:         { label: 'OCR Processing',    color: 'blue',   icon: '🔄', phase: 'ocr' },
  ocr_complete:           { label: 'OCR Complete',      color: 'teal',   icon: '✓',  phase: 'extraction' },
  ocr_failed:             { label: 'OCR Failed',        color: 'red',    icon: '✕',  phase: 'ocr' },
  processing_extraction:  { label: 'Extracting',        color: 'blue',   icon: '🔄', phase: 'extraction' },
  pending_review:         { label: 'Needs Review',      color: 'amber',  icon: '👁',  phase: 'review' },
  rejected:                { label: 'Rejected',           color: 'red',    icon: '✕',   phase: 'errors' },
  extracted:              { label: 'Extracted',         color: 'teal',   icon: '✓',  phase: 'done' },
  approved:               { label: 'Approved',          color: 'green',  icon: '✓',  phase: 'done' },
  published:              { label: 'Published',         color: 'green',  icon: '✓',  phase: 'done' },
  extraction_failed:      { label: 'Extraction Failed', color: 'red',    icon: '✕',  phase: 'extraction' },
  failed:                 { label: 'Failed',            color: 'red',    icon: '✕',  phase: 'error' },
  // B4. Until this slice neither had an entry, so a held duplicate rendered as
  // the raw slug `duplicate_pending_review` in grey — which is what an operator
  // saw on 2026-09-07 when they went looking for their missing scan.
  duplicate_pending_review: { label: 'Held — duplicate',    color: 'amber', icon: '⏸', phase: 'review' },
  duplicate_discarded:      { label: 'Discarded duplicate', color: 'gray',  icon: '✕', phase: 'done' },
  // Slice 3c: a batch whose proposed split is held for a person before any of
  // its documents are created. Nothing writes this status until 3c-2's gate.
  pending_boundary_review:  { label: 'Awaiting split review', color: 'amber', icon: '✂', phase: 'review' },
};

// A document whose publish failed. Its pipeline status is left as it was
// (usually pending_review), so delivery is read from publishStatus: it belongs
// in Errors, and NOT in Needs Review, where there is nothing left to review.
export const isPublishFailed = (d) => d?.publishStatus === 'failed';
// A document whose workflow run FAILED (a node failed, or the run was
// interrupted and the run sweep failed it) and that has not been published.
// Its pipeline status is whatever the run left behind, so the run's own status
// is what says something went wrong. A published document is done, whatever an
// old run of it says.
export const isRunFailed = (d) => d?.workflow?.executionStatus === 'failed' && d?.status !== 'published';
const REVIEW_STATUSES = ['needs_review', 'pending_review'];
export const isNeedsReview = (d) => REVIEW_STATUSES.includes(d?.status) && !isPublishFailed(d) && !isRunFailed(d);
// "Retry failed step" on a row: an admin, a failed run to retry, and not a
// failed publish (that one has its own retry on the document).
export const canRetryFailedRunStep = (d, isAdmin) =>
  Boolean(isAdmin) && isRunFailed(d) && !isPublishFailed(d) && Boolean(d?.workflow?.executionId);
// ONE predicate for the Errors tab and its count, so a document that is both a
// failed publish and a failed run is listed once.
export const isErrorDocument = (d) =>
  ['ocr_failed', 'extraction_failed', 'failed'].includes(d?.status) || isPublishFailed(d) || isRunFailed(d);
// Nothing is working on a document whose run failed: it is an error, not in progress.
export const isProcessingDocument = (d) =>
  ['pending_ocr', 'processing_ocr', 'ocr_complete', 'processing_extraction'].includes(d?.status) && !isRunFailed(d);
const PUBLISH_FAILED_STATUS = { label: 'Publish failed', color: 'red', icon: '✕', phase: 'error' };
const RUN_FAILED_STATUS = { label: 'Workflow run failed', color: 'red', icon: '✕', phase: 'error' };
const TAB_KEYS = ['all', 'processing', 'review', 'held', 'completed', 'errors'];

// Multi-document containers. Their rows open the split review page, never
// ReviewDetailPage, whose action bar does not apply to a container.
const CONTAINER_VIEW_STATUSES = ['split', 'pending_boundary_review'];

const PATHWAY_LABELS = {
  A: { label: 'Template Only',   desc: 'Zone-based extraction', color: '#059669' },
  B: { label: 'AI-Assisted',     desc: 'Template + Claude line items', color: '#7c3aed' },
  C: { label: 'AI-Assisted',     desc: 'Schema-first AI', color: '#7c3aed' },
  D: { label: 'AI-Primary',      desc: 'Full Claude extraction', color: '#dc2626' },
};

const FILTER_TABS = [
  { key: 'all',        label: 'All' },
  { key: 'processing', label: 'Processing' },
  { key: 'review',     label: 'Needs Review' },
  // B4. BETWEEN review and completed, deliberately: a held duplicate is waiting
  // on a person exactly as a review is, and it is the only thing in the product
  // that was waiting on one with nowhere to wait. The Review queue could not
  // show it — that queue is keyed on workflow executions and a parked duplicate
  // has none — so this tab IS the reviewer signal the decision calls for.
  { key: 'held',       label: 'Held' },
  { key: 'completed',  label: 'Completed' },
  { key: 'errors',     label: 'Errors' },
];

const POLL_INTERVAL = 5000; // 5 seconds — faster than review queue since we're watching active processing

// ── Helpers ──

function relativeTime(isoString) {
  if (!isoString) return '';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 10) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatCost(usd) {
  if (!usd) return null;
  const val = parseFloat(usd);
  if (val === 0) return '$0.00';
  if (val < 0.01) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(2)}`;
}

function getPhaseProgress(status) {
  const phases = ['ocr', 'extraction', 'review', 'done'];
  const cfg = STATUS_CONFIG[status] || {};
  const idx = phases.indexOf(cfg.phase || 'ocr');
  return { current: idx, total: phases.length, phase: cfg.phase || 'unknown' };
}

function filterDocuments(docs, filterKey) {
  switch (filterKey) {
    case 'processing':
      return docs.filter(isProcessingDocument);
    case 'review':
      return docs.filter(isNeedsReview);
    // HELD ONLY, never also under Needs Review. The two are different jobs: a
    // review is "check what we extracted", a hold is "decide whether we should
    // have taken this at all", and the actions are different. A document in both
    // tabs would be one a reviewer opens from the wrong place.
    case 'held':
      return docs.filter(d => d.status === 'duplicate_pending_review');
    case 'completed':
      return docs.filter(d =>
        ['extracted', 'approved', 'published', 'duplicate_discarded'].includes(d.status)
      );
    case 'errors':
      return docs.filter(d =>
        isErrorDocument(d) ||
        d.ocr?.error || d.extraction?.error || d.activeJob?.status === 'failed'
      );
    default:
      return docs;
  }
}

// ── Main Component ──

const DEFAULT_COLUMNS = ['status', 'doc_type', 'workflow', 'pipeline', 'results', 'cost', 'time', 'actions'];

const DEFAULT_FILTER_STATE = {
  search: '',
  docType: '',
  pipeline: '',
  sortBy: 'newest',
  columnSort: null,
  columnFilters: {},
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'cost_desc', label: 'Cost: high to low' },
  { value: 'cost_asc', label: 'Cost: low to high' },
  { value: 'vendor_az', label: 'Vendor name: A\u2013Z' },
];

const PIPELINE_OPTIONS = [
  { value: '', label: 'All Pipelines' },
  { value: 'A', label: 'A: Template' },
  { value: 'B', label: 'B: Hybrid' },
  { value: 'D', label: 'D: AI-Primary' },
];

const COLUMN_META = {
  status: {
    label: 'Status', filterType: 'discrete', sortable: true,
    getValue: d => d.status || '',
    getDisplayLabel: v => STATUS_CONFIG[v]?.label || v,
  },
  doc_type: {
    label: 'Doc Type', filterType: 'discrete', sortable: true,
    getValue: d => d.documentType || '',
    getDisplayLabel: v => formatDocType(v),
  },
  workflow: {
    label: 'Workflow', filterType: 'discrete', sortable: true,
    getValue: d => {
      const n = d.workflow?.workflowName;
      return (n && n !== '__ai_fallback_sentinel__') ? n : '';
    },
  },
  pipeline: {
    label: 'Pipeline', filterType: 'discrete', sortable: true,
    getValue: d => d.routing?.pathway || '',
    getDisplayLabel: v => v ? `${v}: ${PATHWAY_LABELS[v]?.label || v}` : '(none)',
  },
  results: { label: 'Results', filterType: null, sortable: false },
  cost: {
    label: 'Cost', filterType: 'numeric', sortable: true,
    getValue: d => parseFloat(d.routing?.claudeCostUsd) || 0,
  },
  time: {
    label: 'Time', filterType: 'date', sortable: true,
    getValue: d => d.createdAt || '',
  },
  actions: { label: 'Actions', filterType: null, sortable: false },
};

function getFieldColumnMeta(fieldKey) {
  if (/total|amount|cost|price|tax|subtotal/i.test(fieldKey)) return { filterType: 'numeric', sortable: true };
  if (/date|due/i.test(fieldKey)) return { filterType: 'date', sortable: true };
  return { filterType: 'text', sortable: true };
}

function getFieldValue(doc, fieldKey) {
  return doc.extractedFields?.[fieldKey] || '';
}

function applySecondaryFilters(docs, filters) {
  let result = docs;

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(d => {
      const vendor = (d.extractedFields?.vendor_name || '').toLowerCase();
      const docType = (d.documentType || '').toLowerCase();
      const filename = (d.filename || '').toLowerCase();
      return vendor.includes(q) || docType.includes(q) || filename.includes(q);
    });
  }

  if (filters.docType) {
    result = result.filter(d => (d.documentType || '') === filters.docType);
  }

  if (filters.pipeline) {
    result = result.filter(d => d.routing?.pathway === filters.pipeline);
  }

  return result;
}

function applyColumnFilters(docs, columnFilters) {
  if (!columnFilters) return docs;
  let result = docs;
  for (const [colKey, fs] of Object.entries(columnFilters)) {
    if (!fs) continue;
    const meta = COLUMN_META[colKey];
    if (meta) {
      if (meta.filterType === 'discrete' && fs.excluded?.length > 0) {
        const ex = new Set(fs.excluded);
        result = result.filter(d => !ex.has(meta.getValue(d)));
      } else if (meta.filterType === 'numeric') {
        if (fs.min != null) result = result.filter(d => meta.getValue(d) >= fs.min);
        if (fs.max != null) result = result.filter(d => meta.getValue(d) <= fs.max);
      } else if (meta.filterType === 'date') {
        if (fs.from) { const f = new Date(fs.from); f.setHours(0,0,0,0); result = result.filter(d => new Date(meta.getValue(d)) >= f); }
        if (fs.to) { const t = new Date(fs.to); t.setHours(23,59,59,999); result = result.filter(d => new Date(meta.getValue(d)) <= t); }
      }
    } else {
      const fm = getFieldColumnMeta(colKey);
      const gv = d => getFieldValue(d, colKey);
      if (fm.filterType === 'text' && fs.text) {
        const q = fs.text.toLowerCase();
        result = result.filter(d => gv(d).toLowerCase().includes(q));
      } else if (fm.filterType === 'discrete' && fs.excluded?.length > 0) {
        const ex = new Set(fs.excluded);
        result = result.filter(d => !ex.has(gv(d)));
      } else if (fm.filterType === 'numeric') {
        if (fs.min != null) result = result.filter(d => (parseFloat(gv(d)) || 0) >= fs.min);
        if (fs.max != null) result = result.filter(d => (parseFloat(gv(d)) || 0) <= fs.max);
      } else if (fm.filterType === 'date') {
        if (fs.from) { const f = new Date(fs.from); f.setHours(0,0,0,0); result = result.filter(d => new Date(gv(d)) >= f); }
        if (fs.to) { const t = new Date(fs.to); t.setHours(23,59,59,999); result = result.filter(d => new Date(gv(d)) <= t); }
      }
    }
  }
  return result;
}

function applySorting(docs, sortBy) {
  const sorted = [...docs];
  switch (sortBy) {
    case 'oldest':
      return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    case 'cost_desc':
      return sorted.sort((a, b) => (parseFloat(b.routing?.claudeCostUsd) || 0) - (parseFloat(a.routing?.claudeCostUsd) || 0));
    case 'cost_asc':
      return sorted.sort((a, b) => (parseFloat(a.routing?.claudeCostUsd) || 0) - (parseFloat(b.routing?.claudeCostUsd) || 0));
    case 'vendor_az':
      return sorted.sort((a, b) => (a.extractedFields?.vendor_name || '').localeCompare(b.extractedFields?.vendor_name || ''));
    case 'newest':
    default:
      return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
}

function applyColumnSort(docs, columnSort) {
  if (!columnSort) return null;
  const { column, direction } = columnSort;
  const mult = direction === 'asc' ? 1 : -1;
  const sorted = [...docs];
  const meta = COLUMN_META[column];
  if (meta) {
    sorted.sort((a, b) => {
      const va = meta.getValue(a), vb = meta.getValue(b);
      if (meta.filterType === 'numeric') return (va - vb) * mult;
      if (meta.filterType === 'date') return (new Date(va) - new Date(vb)) * mult;
      return String(va).localeCompare(String(vb)) * mult;
    });
  } else {
    const fm = getFieldColumnMeta(column);
    sorted.sort((a, b) => {
      const va = getFieldValue(a, column), vb = getFieldValue(b, column);
      if (fm.filterType === 'numeric') return ((parseFloat(va) || 0) - (parseFloat(vb) || 0)) * mult;
      if (fm.filterType === 'date') return (new Date(va) - new Date(vb)) * mult;
      return String(va).localeCompare(String(vb)) * mult;
    });
  }
  return sorted;
}

function isColFilterActive(colKey, columnFilters) {
  const fs = columnFilters?.[colKey];
  if (!fs) return false;
  if (fs.excluded?.length > 0) return true;
  if (fs.text) return true;
  if (fs.min != null || fs.max != null) return true;
  if (fs.from || fs.to) return true;
  return false;
}

function countActiveFilters(filters) {
  let count = 0;
  if (filters.search) count++;
  if (filters.docType) count++;
  if (filters.pipeline) count++;
  if (filters.sortBy !== 'newest') count++;
  if (filters.columnFilters) {
    for (const colKey of Object.keys(filters.columnFilters)) {
      if (isColFilterActive(colKey, filters.columnFilters)) count++;
    }
  }
  if (filters.columnSort) count++;
  return count;
}

const ProcessingMonitorPage = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch');

  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [worker, setWorker] = useState(null);
  const [retentionDays, setRetentionDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-document reasons from the last bulk delete. Kept WITH the banner message
  // they explain, and rendered only while that message is the one showing — any
  // other error that replaces it must not inherit a stale list.
  const [bulkDeleteReport, setBulkDeleteReport] = useState(null);
  /** The held document whose action is in flight, so one row can disable its own buttons. */
  const [duplicateActing, setDuplicateActing] = useState(null);
  // ?tab=errors (a notification's link) opens that tab; otherwise the saved default.
  const [filter, setFilter] = useState(() => {
    const requested = searchParams.get('tab');
    return TAB_KEYS.includes(requested) ? requested : (localStorage.getItem('pm_default_tab') || 'review');
  });
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [fieldLibrary, setFieldLibrary] = useState([]);
  const [tabFilters, setTabFilters] = useState({});
  const [openFilterCol, setOpenFilterCol] = useState(null);
  const [filterPopoverPos, setFilterPopoverPos] = useState({ top: 0, left: 0 });
  const pendingFilterRef = useRef(null);
  const pollRef = useRef(null);
  const mountTimeRef = useRef(new Date().toISOString());
  const columnPickerRef = useRef(null);
  const filterPopoverRef = useRef(null);

  const currentFilters = tabFilters[filter] || DEFAULT_FILTER_STATE;

  const updateFilter = useCallback((key, value) => {
    setTabFilters(prev => ({
      ...prev,
      [filter]: { ...(prev[filter] || DEFAULT_FILTER_STATE), [key]: value },
    }));
  }, [filter]);

  const clearAllFilters = useCallback(() => {
    setTabFilters(prev => ({ ...prev, [filter]: { ...DEFAULT_FILTER_STATE } }));
    setOpenFilterCol(null);
  }, [filter]);

  const handleColumnSort = useCallback((colKey) => {
    setTabFilters(prev => {
      const cur = prev[filter] || DEFAULT_FILTER_STATE;
      const cs = cur.columnSort;
      let next;
      if (cs?.column === colKey) {
        next = cs.direction === 'asc' ? { column: colKey, direction: 'desc' } : null;
      } else {
        next = { column: colKey, direction: 'asc' };
      }
      return { ...prev, [filter]: { ...cur, columnSort: next } };
    });
  }, [filter]);

  const handleOpenFilter = useCallback((colKey, event) => {
    event.stopPropagation();
    if (openFilterCol === colKey) {
      if (pendingFilterRef.current !== undefined) {
        setTabFilters(prev => {
          const cur = prev[filter] || DEFAULT_FILTER_STATE;
          const cf = { ...cur.columnFilters };
          if (pendingFilterRef.current === null) delete cf[colKey]; else cf[colKey] = pendingFilterRef.current;
          return { ...prev, [filter]: { ...cur, columnFilters: cf } };
        });
        pendingFilterRef.current = undefined;
      }
      setOpenFilterCol(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setFilterPopoverPos({
      top: rect.bottom + 4,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 280)),
    });
    setOpenFilterCol(colKey);
    pendingFilterRef.current = undefined;
  }, [openFilterCol, filter]);

  const handleApplyColumnFilter = useCallback((colKey, filterState) => {
    setTabFilters(prev => {
      const cur = prev[filter] || DEFAULT_FILTER_STATE;
      const cf = { ...cur.columnFilters };
      if (filterState === null) delete cf[colKey]; else cf[colKey] = filterState;
      return { ...prev, [filter]: { ...cur, columnFilters: cf } };
    });
    pendingFilterRef.current = undefined;
    setOpenFilterCol(null);
  }, [filter]);

  const handleClearColumnFilter = useCallback((colKey) => {
    handleApplyColumnFilter(colKey, null);
  }, [handleApplyColumnFilter]);

  useEffect(() => {
    if (!openFilterCol) return;
    const handleClickOutside = (e) => {
      if (filterPopoverRef.current && !filterPopoverRef.current.contains(e.target)) {
        if (e.target.closest('.pm-col-filter-btn')) return;
        if (pendingFilterRef.current !== undefined) {
          setTabFilters(prev => {
            const cur = prev[filter] || DEFAULT_FILTER_STATE;
            const cf = { ...cur.columnFilters };
            if (pendingFilterRef.current === null) delete cf[openFilterCol]; else cf[openFilterCol] = pendingFilterRef.current;
            return { ...prev, [filter]: { ...cur, columnFilters: cf } };
          });
          pendingFilterRef.current = undefined;
        }
        setOpenFilterCol(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') { pendingFilterRef.current = undefined; setOpenFilterCol(null); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEscape); };
  }, [openFilterCol, filter]);

  // Load column preferences from localStorage
  const getTenantId = () => {
    try {
      const session = JSON.parse(localStorage.getItem('supabase.auth.token') || '{}');
      return session?.user?.user_metadata?.tenant_id || 'default';
    } catch {
      return 'default';
    }
  };

  const [columnPrefs, setColumnPrefs] = useState(() => {
    try {
      const tenantId = getTenantId();
      const saved = localStorage.getItem(`pm_column_prefs_${tenantId}`);
      if (saved) {
        // normalize handles legacy prefs objects without columnWidths
        return normalizeColumnPrefs(JSON.parse(saved), DEFAULT_COLUMNS);
      }
    } catch (err) {
      console.warn('[PM] Failed to load column prefs:', err);
    }
    return normalizeColumnPrefs(null, DEFAULT_COLUMNS);
  });

  // Save column preferences to localStorage
  const saveColumnPrefs = useCallback((prefs) => {
    try {
      const tenantId = getTenantId();
      localStorage.setItem(`pm_column_prefs_${tenantId}`, JSON.stringify(prefs));
      setColumnPrefs(prefs);
    } catch (err) {
      console.error('[PM] Failed to save column prefs:', err);
    }
  }, []);

  // ── Excel-style column resize ──
  // During the drag the <col> elements are mutated directly (no React state,
  // no jitter); the commit happens once on mouseup. The FIRST commit persists
  // a snapshot of ALL current column widths, so the switch to
  // table-layout: fixed cannot shift the columns that were not dragged.
  const resizeCleanupRef = useRef(null);
  useEffect(() => () => { if (resizeCleanupRef.current) resizeCleanupRef.current(); }, []);

  const resetColumnWidth = useCallback((colKey) => {
    saveColumnPrefs(clearColumnWidth(columnPrefs, colKey));
  }, [columnPrefs, saveColumnPrefs]);

  const startColumnResize = useCallback((e, colKey) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const table = th && th.closest('table');
    if (!table) return;
    const cols = Array.from(table.querySelectorAll('colgroup col'));
    const headerCells = Array.from(table.querySelectorAll('thead th'));

    // Snapshot every column's rendered width (header cells and cols are
    // index-aligned) so the commit is layout-stable.
    const snapshot = {};
    headerCells.forEach((cell, i) => {
      const key = cols[i] && cols[i].dataset.colkey;
      if (key) snapshot[key] = Math.round(cell.getBoundingClientRect().width);
    });

    const colEl = cols.find(c => c.dataset.colkey === colKey);
    if (!colEl || !snapshot[colKey]) return;

    // Pin the current geometry and switch to fixed layout for the drag.
    cols.forEach(c => {
      const k = c.dataset.colkey;
      if (snapshot[k]) c.style.width = `${snapshot[k]}px`;
    });
    table.style.tableLayout = 'fixed';

    const startX = e.clientX;
    const startWidth = snapshot[colKey];
    let finalWidth = startWidth;
    let raf = null;

    const onMove = (ev) => {
      finalWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (ev.clientX - startX));
      if (raf == null) {
        raf = requestAnimationFrame(() => {
          colEl.style.width = `${finalWidth}px`;
          raf = null;
        });
      }
    };
    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (raf != null) cancelAnimationFrame(raf);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // React state (pm-table--fixed + <col> style widths) takes over after
      // the commit re-render.
      table.style.tableLayout = '';
      resizeCleanupRef.current = null;
    };
    const onUp = () => {
      cleanup();
      saveColumnPrefs(mergeColumnWidths(columnPrefs, { ...snapshot, [colKey]: finalWidth }));
    };
    resizeCleanupRef.current = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [columnPrefs, saveColumnPrefs]);

  // Load field library for column picker
  useEffect(() => {
    const loadFieldLibrary = async () => {
      try {
        // Capture-side column picker over extracted fields — scope to capture so a
        // library-only field never appears as a selectable extraction column.
        const response = await captureAPI.getFieldLibrary('capture');
        // Unwrap response - handle both { fields: [...] } and bare array
        const arr = Array.isArray(response) ? response : (response?.fields || []);
        // Deduplicate by field_key since there's one row per field_key × doc_type
        const unique = [...new Map(arr.map(f => [f.field_key, f])).values()];
        setFieldLibrary(unique);
      } catch (err) {
        console.warn('[PM] Failed to load field library:', err);
      }
    };
    loadFieldLibrary();
  }, []);

  const loadDocuments = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (batchId) params.batchId = batchId;
      // If no batch filter, only show recent documents (last 24 hours)
      if (!batchId) {
        // No time filter — show all documents regardless of age
      }

      const res = await captureAPI.processingMonitor.getDocuments(params);
      if (res.ok) {
        setDocuments(res.documents || []);
        setSummary(res.summary || null);
        setWorker(res.worker || null);
        if (res.retentionDays !== undefined) setRetentionDays(res.retentionDays);
      } else {
        throw new Error(res.error || 'Failed to load documents');
      }
    } catch (err) {
      if (!silent) {
        const errInfo = handleAPIError(err, 'Processing Monitor');
        setError(errInfo.message);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [batchId]);

  // ── B4: the two decisions a reviewer can make about a held duplicate ──────
  //
  // ONE CONFIRM EACH, and they say different things because the consequences
  // are different. Process anyway spends OCR and a workflow run on a document
  // the system believes it already has; Discard moves a customer's file to their
  // recycle bin. Neither is undoable from this page, so neither happens on a
  // single click.
  //
  // Both RELOAD rather than patch the row: the server does several things
  // (status, job, execution, the file) and the reloaded list is the only account
  // of what actually happened. A locally patched row would show the outcome the
  // click hoped for.
  const handleProcessDuplicate = useCallback(async (doc) => {
    if (!window.confirm(
      `Process "${doc.filename}" anyway? It will be read and run through its workflow like any other document, and its original file will move to the Processed folder when that finishes.`,
    )) return;
    setDuplicateActing(doc.id);
    setError(null);
    try {
      await captureAPI.processingMonitor.processDuplicate(doc.id);
      await loadDocuments();
    } catch (err) {
      setError(handleAPIError(err, 'Processing Monitor').message);
    } finally {
      setDuplicateActing(null);
    }
  }, [loadDocuments]);

  const handleDiscardDuplicate = useCallback(async (doc) => {
    if (!window.confirm(
      `Discard "${doc.filename}"? The original file will be moved to your OneDrive recycle bin, where you can still recover it.`,
    )) return;
    setDuplicateActing(doc.id);
    setError(null);
    try {
      await captureAPI.processingMonitor.discardDuplicate(doc.id);
      await loadDocuments();
    } catch (err) {
      setError(handleAPIError(err, 'Processing Monitor').message);
    } finally {
      setDuplicateActing(null);
    }
  }, [loadDocuments]);

  // Initial load
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Auto-poll — faster interval when documents are actively processing
  useEffect(() => {
    const hasActiveProcessing = documents.some(d =>
      ['pending_ocr', 'processing_ocr', 'processing_extraction'].includes(d.status)
    );
    const interval = hasActiveProcessing ? POLL_INTERVAL : POLL_INTERVAL * 3;

    pollRef.current = setInterval(() => loadDocuments(true), interval);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadDocuments, documents]);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filter]);

  // ── Actions ──

  const handleRetryOcr = async (docId) => {
    setRetryingIds(prev => new Set(prev).add(docId));
    try {
      await captureAPI.processingMonitor.retryOcr(docId);
      await loadDocuments(true);
    } catch (err) {
      const errInfo = handleAPIError(err, 'Retry OCR');
      setError(errInfo.message);
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  const handleRetryExtraction = async (docId) => {
    setRetryingIds(prev => new Set(prev).add(docId));
    try {
      await captureAPI.processingMonitor.retryExtraction(docId);
      await loadDocuments(true);
    } catch (err) {
      const errInfo = handleAPIError(err, 'Retry Extraction');
      setError(errInfo.message);
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  // Re-run a failed workflow run from the step it failed at (admin-only on the
  // server). A refusal, or a retry that fails again, is shown with its reason.
  const handleRetryFailedStep = async (doc) => {
    const executionId = doc.workflow?.executionId;
    if (!executionId) return;
    setRetryingIds(prev => new Set(prev).add(doc.id));
    try {
      const result = await captureAPI.workflows.retryFailedNode(executionId);
      await loadDocuments(true);
      // After the reload, which clears the banner: the run failed again.
      if (!result?.ok) setError(result?.error || 'The retry failed.');
    } catch (err) {
      const errInfo = handleAPIError(err, 'Retry failed step');
      setError(errInfo.message);
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  // Unstick a document that is stuck in processing_extraction / processing_ocr.
  // Resets the document back through the extraction pipeline via the reclassify endpoint.
  const handleReextract = async (docId) => {
    setRetryingIds(prev => new Set(prev).add(docId));
    try {
      await reclassifyDocument(docId, 'reextract');
      await loadDocuments(true);
    } catch (err) {
      const errInfo = handleAPIError(err, 'Re-extract');
      setError(errInfo.message);
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  const handleToggleRetain = async (doc) => {
    const newValue = !doc.retainIndefinitely;
    try {
      await captureAPI.processingMonitor.setRetainIndefinitely(doc.id, newValue);
      setDocuments(prev => prev.map(d =>
        d.id === doc.id ? { ...d, retainIndefinitely: newValue } : d
      ));
    } catch (err) {
      const errInfo = handleAPIError(err, 'Toggle Retain');
      setError(errInfo.message);
    }
  };

  const handleDeleteDocument = async (doc) => {
    const confirmed = window.confirm(
      `Permanently delete "${doc.filename}"?\n\nThis will remove the document and all associated OCR results, extraction data, and routing history. This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingIds(prev => new Set(prev).add(doc.id));
    try {
      await captureAPI.processingMonitor.deleteDocument(doc.id);
      // Remove from local state immediately
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    } catch (err) {
      const errInfo = handleAPIError(err, 'Delete Document');
      setError(errInfo.message);
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  const handleViewDocument = (doc) => {
    // A split container is verified on its own page (slice 3b). ReviewDetailPage
    // would open it as a document, with an action bar that does not apply to a
    // container — Reject there overwrites its status.
    if (CONTAINER_VIEW_STATUSES.includes(doc.status)) {
      navigate(`/split-review/${doc.id}`, { state: { fromProcessingMonitor: true } });
      return;
    }
    const isReviewStatus = ['needs_review', 'pending_review'].includes(doc.status);
    const executionId = doc.workflow?.executionId;

    // A failed publish opens the run it failed in (the page shows the reason
    // and Retry publish). It is not part of the review queue, so no queue rides
    // along; a document with no run opens by its own id.
    if (isPublishFailed(doc) || isRunFailed(doc)) {
      navigate(executionId ? `/review/${executionId}` : `/documents/${doc.id}`, {
        state: { fromProcessingMonitor: true },
      });
      return;
    }

    if (isReviewStatus) {
      // Build queue from ALL pending_review docs, ordered by created_at ascending (oldest first)
      const queueDocs = documents
        .filter(isNeedsReview)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      const queueDocIds = queueDocs.map(d => d.id);

      // Use execution ID if available, otherwise document ID
      const reviewId = executionId || doc.id;
      navigate(`/review/${reviewId}`, {
        state: { siblingIds: executionId ? queueDocs.filter(d => d.workflow?.executionId).map(d => d.workflow.executionId) : [], queueDocIds, fromQueue: true },
      });
    } else {
      const reviewDocIds = documents
        .filter(isNeedsReview)
        .map(d => d.id);
      navigate(`/documents/${doc.id}`, {
        state: {
          batchDocIds: reviewDocIds,
          batchName: 'Needs Review',
          fromProcessingMonitor: true,
        },
      });
    }
  };

  const toggleExpand = (docId) => {
    setExpandedId(prev => prev === docId ? null : docId);
  };

  // ── Computed values (must be above handlers that reference them) ──
  const tabFilteredDocs = filterDocuments(documents, filter);
  const secondaryFiltered = applySecondaryFilters(tabFilteredDocs, currentFilters);
  const afterColumnFilters = applyColumnFilters(secondaryFiltered, currentFilters.columnFilters);
  const columnSorted = currentFilters.columnSort ? applyColumnSort(afterColumnFilters, currentFilters.columnSort) : null;
  const filteredDocs = columnSorted || applySorting(afterColumnFilters, currentFilters.sortBy);
  const activeFilterCount = countActiveFilters(currentFilters);

  const distinctDocTypes = useMemo(() => {
    const types = new Set();
    tabFilteredDocs.forEach(d => { if (d.documentType) types.add(d.documentType); });
    return [...types].sort();
  }, [tabFilteredDocs]);

  const heldCount = documents.filter(d => d.status === 'duplicate_pending_review').length;

  const errorCount = documents.filter(isErrorDocument).length;
  const processingCount = documents.filter(isProcessingDocument).length;
  const reviewCount = documents.filter(isNeedsReview).length;

  // ── Selection ──

  const toggleSelect = (docId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredDocs.map(d => d.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (count === 0) return;

    const confirmed = window.confirm(
      `Permanently delete ${count} document${count > 1 ? 's' : ''}?\n\nAll associated OCR results, extraction data, and routing history will be removed. This cannot be undone.`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    setBulkDeleteReport(null);
    const idsToDelete = [...selectedIds];
    setDeletingIds(prev => new Set([...prev, ...idsToDelete]));

    try {
      const result = await captureAPI.processingMonitor.bulkDelete(idsToDelete);
      const deletedSet = new Set(
        (result.results || []).filter(r => r.ok).map(r => r.id)
      );
      setDocuments(prev => prev.filter(d => !deletedSet.has(d.id)));
      setSelectedIds(new Set());

      if (result.failed > 0) {
        const message = `Deleted ${result.deleted} document${result.deleted !== 1 ? 's' : ''}. ${result.failed} could not be deleted:`;
        setError(message);
        setBulkDeleteReport({ message, failures: describeBulkDeleteFailures(result.results, documents) });
      }
    } catch (err) {
      const errInfo = handleAPIError(err, 'Bulk Delete');
      setError(errInfo.message);
    } finally {
      setDeletingIds(new Set());
      setBulkDeleting(false);
    }
  };

  const COLUMN_LABELS = { status: 'Status', doc_type: 'Doc Type', workflow: 'Workflow', pipeline: 'Pipeline', results: 'Results', cost: 'Cost', time: 'Time', actions: 'Actions' };

  const renderSortableHeader = (colKey, label, isFieldCol) => {
    const meta = isFieldCol ? getFieldColumnMeta(colKey) : COLUMN_META[colKey];
    const resizeHandle = (
      <span
        className="pm-col-resize-handle"
        onMouseDown={(e) => startColumnResize(e, colKey)}
        onDoubleClick={(e) => { e.stopPropagation(); resetColumnWidth(colKey); }}
        onClick={(e) => e.stopPropagation()}
        title="Drag to resize \u00b7 double-click to reset"
      />
    );
    if (!meta || !meta.sortable) {
      return <th key={colKey} className={`pm-th-${colKey} pm-th--resizable`}>{label}{resizeHandle}</th>;
    }
    const cs = currentFilters.columnSort;
    const isSorted = cs?.column === colKey;
    const sortDir = isSorted ? cs.direction : null;
    const hasFilter = isColFilterActive(colKey, currentFilters.columnFilters);
    return (
      <th key={colKey} className={`pm-th-${colKey} pm-th--resizable pm-th-sortable ${hasFilter ? 'pm-th-filtered' : ''}`}>
        <div className="pm-th-inner">
          <span className="pm-th-label" onClick={() => handleColumnSort(colKey)} title={`Sort by ${label}`}>
            {label}
            {isSorted ? (
              <span className="pm-sort-arrow">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>
            ) : (
              <span className="pm-sort-arrow pm-sort-arrow--hover">{'\u2195'}</span>
            )}
          </span>
          <button
            className={`pm-col-filter-btn ${hasFilter ? 'pm-col-filter-btn--active' : ''}`}
            onClick={(e) => handleOpenFilter(colKey, e)}
            title={`Filter ${label}`}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M0 1h10L6.5 5v3.5L3.5 10V5L0 1z" /></svg>
          </button>
        </div>
        {resizeHandle}
      </th>
    );
  };

  // Shared <colgroup> for both table renders. The leading 'select' column is
  // never user-resizable, but its snapshot width IS persisted (key 'select')
  // so a fixed-layout table cannot hand it the leftover space.
  const tableFixed = hasAnyColumnWidth(columnPrefs);
  const tableClassName = `pm-table ${tableFixed ? 'pm-table--fixed' : ''}`;
  const renderColGroup = () => (
    <colgroup>
      {['select', ...columnPrefs.fixedColumns, ...columnPrefs.fieldColumns].map(k => {
        const w = columnPrefs.columnWidths?.[k];
        return <col key={k} data-colkey={k} style={w ? { width: `${w}px` } : undefined} />;
      })}
    </colgroup>
  );

  return (
    <div className="pm-page">
      {/* Header */}
      <div className="pm-header">
        <div className="pm-header-left">
          <h1>Processing Monitor</h1>
          <span className={`pm-count-badge ${documents.length === 0 ? 'empty' : ''}`}>
            {documents.length}
          </span>
          {processingCount > 0 && (
            <span className="pm-active-indicator">
              <span className="pm-pulse" />
              {processingCount} active
            </span>
          )}
        </div>
        <div className="pm-header-right">
          {summary && summary.totalClaudeCost > 0 && (
            <span className="pm-cost-badge" title="Total Claude API cost for visible documents">
              AI Cost: {formatCost(summary.totalClaudeCost)}
            </span>
          )}
          {worker && (
            <span className="pm-worker-badge" title={`OCR Worker: ${worker.activeJobs}/${worker.maxConcurrentJobs} active jobs`}>
              Worker: {worker.activeJobs}/{worker.maxConcurrentJobs}
            </span>
          )}
          <button
            className="pm-set-default-btn"
            onClick={() => { localStorage.setItem('pm_default_tab', filter); }}
            title={`Save "${FILTER_TABS.find(t => t.key === filter)?.label || filter}" as default tab`}
          >
            Set as Default
          </button>
          <button
            className="pm-columns-btn"
            onClick={() => setColumnPickerOpen(!columnPickerOpen)}
            title="Configure columns"
          >
            Columns
          </button>
          <button className="pm-refresh-btn" onClick={() => loadDocuments()} title="Refresh now">
            ↻
          </button>
        </div>
      </div>

      {/* Column Picker */}
      {columnPickerOpen && (
        <ColumnPicker
          columnPrefs={columnPrefs}
          fieldLibrary={fieldLibrary}
          onSave={(prefs) => {
            const oldFieldCols = new Set(columnPrefs.fieldColumns);
            const newFieldCols = new Set(prefs.fieldColumns);
            const oldFixedCols = new Set(columnPrefs.fixedColumns);
            const newFixedCols = new Set(prefs.fixedColumns);
            const removedCols = [...oldFieldCols].filter(c => !newFieldCols.has(c)).concat([...oldFixedCols].filter(c => !newFixedCols.has(c)));
            if (removedCols.length > 0) {
              setTabFilters(prev => {
                const cur = prev[filter] || DEFAULT_FILTER_STATE;
                const cf = { ...cur.columnFilters };
                removedCols.forEach(c => delete cf[c]);
                return { ...prev, [filter]: { ...cur, columnFilters: cf } };
              });
            }
            // Widths ride along with the picker's prefs object (Apply), and a
            // picker "Reset to Default" intentionally clears them to all-auto.
            saveColumnPrefs({ ...prefs, columnWidths: prefs.columnWidths || {} });
          }}
          onClose={() => setColumnPickerOpen(false)}
          pickerRef={columnPickerRef}
        />
      )}

      {/* Filter tabs */}
      <div className="pm-tabs">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            className={`pm-tab ${filter === tab.key ? 'active' : ''}`}
            onClick={() => setFilter(tab.key)}
          >
            {tab.label}
            {tab.key === 'errors' && errorCount > 0 && (
              <span className="pm-tab-badge pm-tab-badge--red">{errorCount}</span>
            )}
            {tab.key === 'processing' && processingCount > 0 && (
              <span className="pm-tab-badge pm-tab-badge--blue">{processingCount}</span>
            )}
            {/* Amber, and only above zero. A held duplicate is a person's
                decision that nothing else in the product will make for them —
                which is exactly what the count on a tab is for. A badge on a
                zero would make an idle system look like it needed attention. */}
            {tab.key === 'held' && heldCount > 0 && (
              <span className="pm-tab-badge pm-tab-badge--amber">{heldCount}</span>
            )}
            {tab.key === 'review' && reviewCount > 0 && (
              <span className="pm-tab-badge pm-tab-badge--amber">{reviewCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filter/Sort toolbar */}
      <div className="pm-filter-toolbar">
        <div className="pm-filter-toolbar-left">
          <input
            type="text"
            className="pm-filter-search"
            placeholder="Search vendor, doc type, filename\u2026"
            value={currentFilters.search}
            onChange={e => updateFilter('search', e.target.value)}
          />
          <select
            className="pm-filter-select"
            value={currentFilters.docType}
            onChange={e => updateFilter('docType', e.target.value)}
          >
            <option value="">All Types</option>
            {distinctDocTypes.map(t => (
              <option key={t} value={t}>{formatDocType(t)}</option>
            ))}
          </select>
          <select
            className="pm-filter-select"
            value={currentFilters.pipeline}
            onChange={e => updateFilter('pipeline', e.target.value)}
          >
            {PIPELINE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="pm-filter-toolbar-right">
          <select
            className="pm-filter-select"
            value={currentFilters.sortBy}
            onChange={e => updateFilter('sortBy', e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {activeFilterCount > 0 && (
            <span className="pm-filter-active-group">
              <span className="pm-filter-badge">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
              <button className="pm-filter-clear-btn" onClick={clearAllFilters}>Clear all</button>
            </span>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="pm-bulk-bar">
          <span className="pm-bulk-count">
            {selectedIds.size} document{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="pm-bulk-actions">
            {/* Cluster 2 / A1: bulk delete is admin-only (destructive,
                tenant-wide). Server enforces via accessPolicy adminOnly. */}
            {isAdmin && (
              <button
                className="pm-bulk-btn pm-bulk-btn--delete"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Deleting\u2026' : `Delete Selected (${selectedIds.size})`}
              </button>
            )}
            <button
              className="pm-bulk-btn pm-bulk-btn--cancel"
              onClick={() => setSelectedIds(new Set())}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="pm-error-banner">
          <span>
            {error}
            {bulkDeleteReport?.message === error && bulkDeleteReport.failures.length > 0 && (
              <ul className="pm-delete-failures">
                {bulkDeleteReport.failures.map((f) => (
                  <li key={f.id}>
                    <strong>{f.filename}</strong> &mdash; {f.reason}
                  </li>
                ))}
              </ul>
            )}
          </span>
          <button onClick={() => { setError(null); setBulkDeleteReport(null); }}>&times;</button>
        </div>
      )}

      {/* Content */}
      <div className="pm-content">
        {loading ? (
          <div className="pm-loading">
            <div className="spinner" />
            <p>Loading processing status...</p>
          </div>
        ) : tabFilteredDocs.length === 0 ? (
          <div className="pm-empty">
            <div className="pm-empty-icon">{filter === 'errors' || filter === 'review' || filter === 'held' ? '✅' : '📄'}</div>
            <h3>
              {filter === 'errors' ? 'No Errors'
                : filter === 'review' ? 'No Documents Awaiting Review'
                  : filter === 'held' ? 'Nothing Held'
                    : 'No Documents'}
            </h3>
            <p>
              {filter === 'errors'
                ? 'All documents are processing normally.'
                : filter === 'review'
                  ? 'All documents have been reviewed.'
                  : filter === 'held'
                    ? 'No duplicates are waiting for a decision.'
                    : batchId
                      ? 'No documents found for this batch.'
                      : 'Upload documents to see processing status here.'}
            </p>
            {!batchId && filter === 'all' && (
              <button className="pm-upload-btn" onClick={() => navigate('/')}>
                Upload Documents
              </button>
            )}
          </div>
        ) : filter === 'held' ? (
          /*
           * A DEDICATED PANEL, not the shared table.
           *
           * The generic table is column-configurable and its action column is
           * shared by every tab; wiring Process anyway / Discard into it would
           * put two destructive-looking buttons on every row of every other tab
           * and make them depend on the user's column preferences to be visible
           * at all.
           *
           * The Held tab also needs four things the shared columns do not carry
           * — the source's display name, and the matched original's filename and
           * capture date — and those exist only on a held document. A panel that
           * shows exactly what the decision needs is smaller than teaching the
           * table about a shape only one tab has.
           */
          <div className="pm-held">
            <p className="pm-held-intro">
              These arrived from a source but match a document already captured, so
              they were held rather than processed. The original file has been moved
              to the source’s Duplicates folder and is waiting on your decision.
            </p>
            {filteredDocs.map(doc => {
              const held = doc.heldDuplicate || {};
              const original = held.matchedOriginal || null;
              const busy = duplicateActing === doc.id;
              return (
                <div className="pm-held-row" key={doc.id}>
                  <div className="pm-held-main">
                    <div className="pm-held-filename">{doc.filename}</div>
                    <div className="pm-held-meta">
                      {held.channelName ? `From ${held.channelName}` : 'From an unknown source'}
                      {' · '}
                      {`Arrived ${relativeTime(doc.createdAt)}`}
                    </div>
                    <div className="pm-held-match">
                      {/* THREE HOPS, ANY OF WHICH MAY BE EMPTY. The match is
                          derived from the ingestion audit trail at read time and
                          is allowed not to resolve — an original that has since
                          been deleted breaks the chain. Saying so is honest;
                          showing a filename for a document that is gone is not,
                          and the reviewer needs to know which case they are in
                          before they discard anything. */}
                      {original
                        ? `Matches ${original.filename}, captured ${relativeTime(original.capturedAt)}`
                        : 'Matching original not found — it may have been deleted since'}
                    </div>
                  </div>
                  <div className="pm-held-actions">
                    <button
                      className="pm-held-btn pm-held-btn--primary"
                      disabled={busy}
                      onClick={() => handleProcessDuplicate(doc)}
                    >
                      {busy ? 'Working…' : 'Process anyway'}
                    </button>
                    <button
                      className="pm-held-btn pm-held-btn--danger"
                      disabled={busy}
                      onClick={() => handleDiscardDuplicate(doc)}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : filteredDocs.length === 0 && activeFilterCount > 0 ? (
          <table className={tableClassName}>
            {renderColGroup()}
            <thead>
              <tr>
                <th className="pm-th-select"><input type="checkbox" disabled /></th>
                {columnPrefs.fixedColumns.map(col => renderSortableHeader(col, COLUMN_LABELS[col] || col, false))}
                {columnPrefs.fieldColumns.map(fk => {
                  const f = fieldLibrary.find(fl => fl.field_key === fk);
                  return renderSortableHeader(fk, f?.label || fk, true);
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={1 + columnPrefs.fixedColumns.length + columnPrefs.fieldColumns.length} className="pm-filter-empty">
                  <div className="pm-filter-empty-inner">
                    <p>No documents match the current filters.</p>
                    <button className="pm-filter-clear-link" onClick={clearAllFilters}>Clear filters</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table className={tableClassName}>
            {renderColGroup()}
            <thead>
              <tr>
                <th className="pm-th-select">
                  <input
                    type="checkbox"
                    checked={filteredDocs.length > 0 && filteredDocs.every(d => selectedIds.has(d.id))}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                {columnPrefs.fixedColumns.map(col => renderSortableHeader(col, COLUMN_LABELS[col] || col, false))}
                {columnPrefs.fieldColumns.map(fk => {
                  const f = fieldLibrary.find(fl => fl.field_key === fk);
                  return renderSortableHeader(fk, f?.label || fk, true);
                })}
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  isAdmin={isAdmin}
                  columnPrefs={columnPrefs}
                  fieldLibrary={fieldLibrary}
                  retentionDays={retentionDays}
                  isExpanded={expandedId === doc.id}
                  isRetrying={retryingIds.has(doc.id)}
                  isDeleting={deletingIds.has(doc.id)}
                  isSelected={selectedIds.has(doc.id)}
                  onToggleSelect={() => toggleSelect(doc.id)}
                  onToggleExpand={() => toggleExpand(doc.id)}
                  onRetryOcr={() => handleRetryOcr(doc.id)}
                  onRetryExtraction={() => handleRetryExtraction(doc.id)}
                  onRetryFailedStep={() => handleRetryFailedStep(doc)}
                  onReextract={() => handleReextract(doc.id)}
                  onView={() => handleViewDocument(doc)}
                  onDelete={() => handleDeleteDocument(doc)}
                  onToggleRetain={() => handleToggleRetain(doc)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Row count + summary footer */}
      {documents.length > 0 && (
        <div className="pm-footer">
          <span className="pm-footer-row-count">
            Showing {filteredDocs.length} of {tabFilteredDocs.length} document{tabFilteredDocs.length !== 1 ? 's' : ''}
          </span>
          {summary && Object.entries(summary.byStatus || {}).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status] || { label: status, color: 'gray' };
            return (
              <span key={status} className={`pm-footer-stat pm-footer-stat--${cfg.color}`}>
                {cfg.label}: {count}
              </span>
            );
          })}
        </div>
      )}

      {/* Column filter popover */}
      {openFilterCol && (
        <ColumnFilterPopover
          ref={filterPopoverRef}
          colKey={openFilterCol}
          meta={COLUMN_META[openFilterCol]}
          fieldMeta={!COLUMN_META[openFilterCol] ? getFieldColumnMeta(openFilterCol) : null}
          isFieldCol={!COLUMN_META[openFilterCol]}
          initialFilter={currentFilters.columnFilters?.[openFilterCol] || null}
          docs={tabFilteredDocs}
          position={filterPopoverPos}
          onApply={(fs) => handleApplyColumnFilter(openFilterCol, fs)}
          onClear={() => handleClearColumnFilter(openFilterCol)}
          onStateChange={(s) => { pendingFilterRef.current = s; }}
        />
      )}
    </div>
  );
};

// ── Document Row Component ──

function getRetentionInfo(doc, retentionDays) {
  if (!retentionDays || !doc.publishedAt) return null;
  if (doc.retainIndefinitely) return { kept: true };
  if (!['published', 'archived'].includes(doc.status)) return null;
  const publishedAt = new Date(doc.publishedAt);
  const deadline = new Date(publishedAt.getTime() + retentionDays * 24 * 60 * 60 * 1000);
  const daysRemaining = Math.ceil((deadline.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysRemaining <= 14) return { daysRemaining, approaching: true };
  return null;
}

function DocumentRow({ doc, isAdmin, columnPrefs, fieldLibrary, retentionDays, isExpanded, isRetrying, isDeleting, isSelected, onToggleSelect, onToggleExpand, onRetryOcr, onRetryExtraction, onRetryFailedStep, onReextract, onView, onDelete, onToggleRetain }) {
  const statusCfg = isPublishFailed(doc)
    ? PUBLISH_FAILED_STATUS
    : isRunFailed(doc)
    ? RUN_FAILED_STATUS
    : STATUS_CONFIG[doc.status] || { label: doc.status, color: 'gray', icon: '?', phase: 'unknown' };
  const pathwayCfg = doc.routing ? PATHWAY_LABELS[doc.routing.pathway] : null;
  const progress = getPhaseProgress(doc.status);
  const hasError = doc.ocr?.error || doc.extraction?.error || doc.activeJob?.status === 'failed';
  // A document whose run failed is not processing, whatever status the run left on it.
  const isProcessing = !isRunFailed(doc) && ['pending_ocr', 'processing_ocr', 'processing_extraction'].includes(doc.status);
  const docIsStuck = isStuck(doc);
  const retention = getRetentionInfo(doc, retentionDays);

  const renderColumnCell = (col) => {
    switch (col) {
      case 'status':
        return (
          <td key="status" className="pm-cell-status">
            <span className={`pm-status-pill pm-status-pill--${statusCfg.color}`}>
              {isProcessing && <span className="pm-status-spinner" />}
              {!isProcessing && <span className="pm-status-icon">{statusCfg.icon}</span>}
              {statusCfg.label}
            </span>
          </td>
        );
      case 'doc_type':
        return (
          <td key="doc_type" className="pm-cell-doc-type">
            {doc.documentType ? (
              <span className="pm-doc-type-badge" title={formatDocType(doc.documentType)}>{formatDocType(doc.documentType)}</span>
            ) : (
              <span className="pm-doc-type-none">—</span>
            )}
          </td>
        );
      case 'workflow':
        return (
          <td key="workflow" className="pm-cell-workflow">
            {doc.workflow?.workflowName && doc.workflow.workflowName !== '__ai_fallback_sentinel__' ? (
              <span className="pm-workflow-badge" title={`${doc.workflow.workflowName} — execution: ${doc.workflow.executionStatus}`}>
                ⚡ {doc.workflow.workflowName}
              </span>
            ) : (
              <span className="pm-workflow-none">—</span>
            )}
          </td>
        );
      case 'pipeline':
        return (
          <td key="pipeline" className="pm-cell-pipeline">
            {pathwayCfg ? (
              <span className="pm-pathway-badge" style={{ borderColor: pathwayCfg.color, color: pathwayCfg.color }}>
                {doc.routing.pathway}: {pathwayCfg.label}
              </span>
            ) : (
              <span className="pm-pipeline-steps">
                <span className={`pm-pip ${progress.current >= 0 ? 'pm-pip--done' : ''} ${progress.phase === 'ocr' && isProcessing ? 'pm-pip--active' : ''}`} title="OCR" />
                <span className={`pm-pip ${progress.current >= 1 ? 'pm-pip--done' : ''} ${progress.phase === 'extraction' && isProcessing ? 'pm-pip--active' : ''}`} title="Extract" />
                <span className={`pm-pip ${progress.current >= 2 ? 'pm-pip--done' : ''}`} title="Review" />
                <span className={`pm-pip ${progress.current >= 3 ? 'pm-pip--done' : ''}`} title="Done" />
              </span>
            )}
          </td>
        );
      case 'results':
        return (
          <td key="results" className="pm-cell-results">
            {doc.extraction?.fieldCount > 0 && (
              <span className="pm-result-tag pm-result-tag--fields">{doc.extraction.fieldCount} fields</span>
            )}
            {doc.extraction?.lineItemCount > 0 && (
              <span className="pm-result-tag pm-result-tag--items">{doc.extraction.lineItemCount} items</span>
            )}
            {doc.extraction?.matchRate && (
              <span className="pm-result-tag pm-result-tag--match">{Math.round(doc.extraction.matchRate * 100)}%</span>
            )}
          </td>
        );
      case 'cost':
        return (
          <td key="cost" className="pm-cell-cost">
            {doc.routing?.claudeCostUsd ? (
              <span className="pm-cost">{formatCost(doc.routing.claudeCostUsd)}</span>
            ) : doc.routing?.pathway === 'A' ? (
              <span className="pm-cost pm-cost--free">$0.00</span>
            ) : null}
          </td>
        );
      case 'time':
        return (
          <td key="time" className="pm-cell-time">
            <span className="pm-time">{relativeTime(doc.createdAt)}</span>
            {retention?.kept && (
              <span className="pm-retention-kept" title="Exempt from retention policy">Kept</span>
            )}
            {retention?.approaching && !retention.kept && (
              <span className="pm-retention-warning" title={`Will be purged in ${retention.daysRemaining} day${retention.daysRemaining !== 1 ? 's' : ''}`}>
                Purge in {retention.daysRemaining}d
              </span>
            )}
          </td>
        );
      case 'actions':
        return (
          <td key="actions" className="pm-cell-actions" onClick={e => e.stopPropagation()}>
            {/* Cluster 2 / A1: retry-OCR and retry-extraction are
                cost-incurring, admin-only (server enforces via accessPolicy
                adminOnly). Re-extract (stuck docs, reclassify endpoint), View,
                Review, Keep stay member-open. */}
            {isAdmin && hasError && !isRetrying && (
              <>
                {(doc.ocr?.error || doc.status === 'ocr_failed') && (
                  <button className="pm-action-btn pm-action-btn--retry" onClick={onRetryOcr} title="Retry OCR">
                    ↻ OCR
                  </button>
                )}
                {(doc.extraction?.error || doc.status === 'extraction_failed') && (
                  <button className="pm-action-btn pm-action-btn--retry" onClick={onRetryExtraction} title="Retry Extraction">
                    ↻ Extract
                  </button>
                )}
                {doc.activeJob?.status === 'failed' && doc.status !== 'ocr_failed' && doc.status !== 'extraction_failed' && (
                  <button className="pm-action-btn pm-action-btn--retry" onClick={onRetryOcr} title="Retry">
                    ↻ Retry
                  </button>
                )}
              </>
            )}
            {/* A failed workflow RUN is retried from the step it failed at:
                admin-only, like the two above. Not offered for a failed
                publish, which is retried from the document (open to members). */}
            {canRetryFailedRunStep(doc, isAdmin) && !isRetrying && (
              <button
                className="pm-action-btn pm-action-btn--retry"
                onClick={onRetryFailedStep}
                title="Run this workflow again from the step it failed at"
              >
                ↻ Retry failed step
              </button>
            )}
            {docIsStuck && !isRetrying && !hasError && (
              <button
                className="pm-action-btn pm-action-btn--stuck"
                onClick={onReextract}
                title="Document appears stuck. Click to restart extraction."
              >
                ↻ Re-extract
              </button>
            )}
            {isRetrying && (
              <span className="pm-retrying">Retrying...</span>
            )}
            {isNeedsReview(doc) && (
              <button className="pm-action-btn pm-action-btn--review" onClick={onView} title="Review document">
                Review
              </button>
            )}
            {!hasError && !isRetrying && !isProcessing && !isNeedsReview(doc) && (
              <button
                className="pm-action-btn pm-action-btn--view"
                onClick={onView}
                title={
                  doc.status === 'split' ? 'Verify how this batch was split'
                    : doc.status === 'pending_boundary_review' ? 'Review where this batch\'s documents begin'
                      : 'View document'
                }
              >
                {doc.status === 'split' ? 'Verify split' : doc.status === 'pending_boundary_review' ? 'Review split' : 'View'}
              </button>
            )}
            {retentionDays && ['published', 'archived'].includes(doc.status) && (
              <button
                className={`pm-action-btn pm-action-btn--retain ${doc.retainIndefinitely ? 'pm-action-btn--retain-active' : ''}`}
                onClick={onToggleRetain}
                title={doc.retainIndefinitely ? 'Remove retention exemption' : 'Keep indefinitely'}
              >
                {doc.retainIndefinitely ? 'Unkeep' : 'Keep'}
              </button>
            )}
            {isDeleting ? (
              <span className="pm-deleting">Deleting…</span>
            ) : (
              <button
                className="pm-action-btn pm-action-btn--delete"
                onClick={onDelete}
                title="Delete document"
              >
                ✕
              </button>
            )}
          </td>
        );
      default:
        return null;
    }
  };

  const renderFieldCell = (fieldKey) => {
    const value = doc.extractedFields?.[fieldKey];
    const displayValue = value ? (String(value).length > 24 ? String(value).substring(0, 24) + '…' : String(value)) : '—';
    return (
      <td key={fieldKey} className="pm-cell-field" title={value || ''}>
        <span className="pm-field-value">{displayValue}</span>
      </td>
    );
  };

  return (
    <>
      <tr
        className={`pm-row ${hasError ? 'pm-row--error' : ''} ${isProcessing ? 'pm-row--processing' : ''} ${docIsStuck ? 'pm-row--stuck' : ''} ${isSelected ? 'pm-row--selected' : ''}`}
        onClick={onToggleExpand}
      >
        <td className="pm-cell-select" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
          />
        </td>
        {columnPrefs.fixedColumns.map(col => renderColumnCell(col))}
        {columnPrefs.fieldColumns.map(fieldKey => renderFieldCell(fieldKey))}
      </tr>

      {/* Expanded detail row */}
      {isExpanded && (
        <tr className="pm-detail-row">
          <td colSpan={1 + columnPrefs.fixedColumns.length + columnPrefs.fieldColumns.length}>
            <DocumentDetail doc={doc} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Document Detail (expanded) Component ──

function DocumentDetail({ doc }) {
  return (
    <div className="pm-detail">
      <div className="pm-detail-grid">
        {/* OCR Section */}
        <div className="pm-detail-section">
          <h4>OCR</h4>
          <dl className="pm-detail-dl">
            <dt>Engine</dt>
            <dd>{doc.ocr?.engine || '—'}</dd>
            <dt>Started</dt>
            <dd>{doc.ocr?.startedAt ? new Date(doc.ocr.startedAt).toLocaleTimeString() : '—'}</dd>
            <dt>Completed</dt>
            <dd>{doc.ocr?.completedAt ? new Date(doc.ocr.completedAt).toLocaleTimeString() : '—'}</dd>
            {doc.ocr?.error && (
              <>
                <dt>Error</dt>
                <dd className="pm-detail-error">{doc.ocr.error}</dd>
              </>
            )}
          </dl>
        </div>

        {/* Extraction Section */}
        <div className="pm-detail-section">
          <h4>Extraction</h4>
          <dl className="pm-detail-dl">
            <dt>Template</dt>
            <dd>{doc.extraction?.templateName || '—'}</dd>
            <dt>Match Rate</dt>
            <dd>{doc.extraction?.matchRate ? `${Math.round(doc.extraction.matchRate * 100)}%` : '—'}</dd>
            <dt>Fields</dt>
            <dd>{doc.extraction?.fieldCount || 0}</dd>
            <dt>Line Items</dt>
            <dd>{doc.extraction?.lineItemCount || 0}</dd>
            {doc.extraction?.error && (
              <>
                <dt>Error</dt>
                <dd className="pm-detail-error">{doc.extraction.error}</dd>
              </>
            )}
          </dl>
        </div>

        {/* Routing Section */}
        <div className="pm-detail-section">
          <h4>Routing</h4>
          {doc.routing ? (
            <dl className="pm-detail-dl">
              <dt>Pathway</dt>
              <dd>
                <strong>{doc.routing.pathway}</strong>
                {PATHWAY_LABELS[doc.routing.pathway] && (
                  <> — {PATHWAY_LABELS[doc.routing.pathway].desc}</>
                )}
              </dd>
              {doc.routing.reasons?.length > 0 && (
                <>
                  <dt>Reasons</dt>
                  <dd>
                    <ul className="pm-detail-reasons">
                      {doc.routing.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </dd>
                </>
              )}
              <dt>Claude Cost</dt>
              <dd>{formatCost(doc.routing.claudeCostUsd) || '$0.00'}</dd>
            </dl>
          ) : (
            <p className="pm-detail-muted">Not yet routed</p>
          )}
        </div>

        {/* Workflow Section */}
        {doc.workflow && (
          <div className="pm-detail-section">
            <h4>Workflow</h4>
            <dl className="pm-detail-dl">
              <dt>Workflow</dt>
              <dd>{doc.workflow.workflowName === '__ai_fallback_sentinel__' ? '—' : (doc.workflow.workflowName || '—')}</dd>
              <dt>Execution Status</dt>
              <dd>{doc.workflow.executionStatus}</dd>
              {doc.workflow.pausedAt && (
                <>
                  <dt>Paused At</dt>
                  <dd>{new Date(doc.workflow.pausedAt).toLocaleString()}</dd>
                </>
              )}
              {doc.workflow.completedAt && (
                <>
                  <dt>Completed At</dt>
                  <dd>{new Date(doc.workflow.completedAt).toLocaleString()}</dd>
                </>
              )}
            </dl>
          </div>
        )}

        {/* Job Section */}
        {doc.activeJob && (
          <div className="pm-detail-section">
            <h4>Active Job</h4>
            <dl className="pm-detail-dl">
              <dt>Type</dt>
              <dd>{doc.activeJob.type}</dd>
              <dt>Status</dt>
              <dd className={doc.activeJob.status === 'failed' ? 'pm-detail-error' : ''}>{doc.activeJob.status}</dd>
              {doc.activeJob.error && (
                <>
                  <dt>Error</dt>
                  <dd className="pm-detail-error">{doc.activeJob.error}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>

      {/* Document ID for debugging */}
      <div className="pm-detail-id">
        ID: <code>{doc.id}</code>
        {doc.batchId && <> · Batch: <code>{doc.batchId}</code></>}
      </div>
    </div>
  );
}

// ── Column Filter Popover Component ──

const ColumnFilterPopover = React.forwardRef(function ColumnFilterPopover({ colKey, meta, fieldMeta, isFieldCol, initialFilter, docs, position, onApply, onClear, onStateChange }, ref) {
  const filterType = meta ? meta.filterType : (fieldMeta?.filterType || 'text');
  const getValue = meta ? meta.getValue : (d => getFieldValue(d, colKey));
  const getLabel = meta?.getDisplayLabel || (v => v || '(empty)');

  const [localExcluded, setLocalExcluded] = useState(() => new Set(initialFilter?.excluded || []));
  const [localText, setLocalText] = useState(initialFilter?.text || '');
  const [localMin, setLocalMin] = useState(initialFilter?.min ?? '');
  const [localMax, setLocalMax] = useState(initialFilter?.max ?? '');
  const [localFrom, setLocalFrom] = useState(initialFilter?.from || '');
  const [localTo, setLocalTo] = useState(initialFilter?.to || '');

  useEffect(() => {
    if (filterType === 'discrete') {
      const fs = localExcluded.size > 0 ? { excluded: [...localExcluded] } : null;
      onStateChange(fs);
    } else if (filterType === 'text') {
      onStateChange(localText ? { text: localText } : null);
    } else if (filterType === 'numeric') {
      const mn = localMin !== '' ? parseFloat(localMin) : null;
      const mx = localMax !== '' ? parseFloat(localMax) : null;
      onStateChange((mn != null || mx != null) ? { min: mn, max: mx } : null);
    } else if (filterType === 'date') {
      onStateChange((localFrom || localTo) ? { from: localFrom, to: localTo } : null);
    }
  }, [localExcluded, localText, localMin, localMax, localFrom, localTo, filterType, onStateChange]);

  const distinctValues = useMemo(() => {
    if (filterType !== 'discrete' && filterType !== 'text') return [];
    const counts = new Map();
    docs.forEach(d => {
      const v = getValue(d);
      counts.set(v, (counts.get(v) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
  }, [docs, getValue, filterType]);

  const handleApply = () => {
    if (filterType === 'discrete') {
      onApply(localExcluded.size > 0 ? { excluded: [...localExcluded] } : null);
    } else if (filterType === 'text') {
      onApply(localText ? { text: localText } : null);
    } else if (filterType === 'numeric') {
      const mn = localMin !== '' ? parseFloat(localMin) : null;
      const mx = localMax !== '' ? parseFloat(localMax) : null;
      onApply((mn != null || mx != null) ? { min: mn, max: mx } : null);
    } else if (filterType === 'date') {
      onApply((localFrom || localTo) ? { from: localFrom, to: localTo } : null);
    }
  };

  const handleClear = () => {
    setLocalExcluded(new Set());
    setLocalText('');
    setLocalMin('');
    setLocalMax('');
    setLocalFrom('');
    setLocalTo('');
    onClear();
  };

  const toggleValue = (val) => {
    setLocalExcluded(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  const allSelected = filterType === 'discrete' && localExcluded.size === 0;
  const noneSelected = filterType === 'discrete' && localExcluded.size === distinctValues.length;

  const toggleAll = () => {
    if (allSelected) {
      setLocalExcluded(new Set(distinctValues.map(([v]) => v)));
    } else {
      setLocalExcluded(new Set());
    }
  };

  const style = { position: 'fixed', top: position.top, left: position.left, zIndex: 200 };

  return (
    <div className="pm-col-popover" style={style} ref={ref}>
      {filterType === 'discrete' && (
        <>
          <div className="pm-col-popover-toggle">
            <label>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
            </label>
          </div>
          <div className="pm-col-popover-list">
            {distinctValues.map(([val, count]) => (
              <label key={String(val)} className="pm-col-popover-item">
                <input type="checkbox" checked={!localExcluded.has(val)} onChange={() => toggleValue(val)} />
                <span>{getLabel(val)} ({count})</span>
              </label>
            ))}
          </div>
        </>
      )}
      {filterType === 'text' && (
        <>
          <input
            type="text"
            className="pm-col-popover-input"
            placeholder={`Filter by ${meta?.label || colKey}\u2026`}
            value={localText}
            onChange={e => setLocalText(e.target.value)}
            autoFocus
          />
          {distinctValues.length > 0 && (
            <div className="pm-col-popover-chips">
              {distinctValues.slice(0, 10).map(([val]) => val && (
                <button key={val} className="pm-col-popover-chip" onClick={() => setLocalText(val)}>{val}</button>
              ))}
            </div>
          )}
        </>
      )}
      {filterType === 'numeric' && (
        <div className="pm-col-popover-range">
          <label>Min $<input type="number" step="0.01" value={localMin} onChange={e => setLocalMin(e.target.value)} placeholder="0.00" /></label>
          <label>Max $<input type="number" step="0.01" value={localMax} onChange={e => setLocalMax(e.target.value)} placeholder="0.00" /></label>
        </div>
      )}
      {filterType === 'date' && (
        <div className="pm-col-popover-range">
          <label>From<input type="date" value={localFrom} onChange={e => setLocalFrom(e.target.value)} /></label>
          <label>To<input type="date" value={localTo} onChange={e => setLocalTo(e.target.value)} /></label>
        </div>
      )}
      <div className="pm-col-popover-actions">
        <button className="pm-col-popover-btn pm-col-popover-btn--clear" onClick={handleClear}>Clear</button>
        <button className="pm-col-popover-btn pm-col-popover-btn--apply" onClick={handleApply}>Apply</button>
      </div>
    </div>
  );
});

// ── Column Picker Component ──

function ColumnPicker({ columnPrefs, fieldLibrary, onSave, onClose, pickerRef }) {
  const [localPrefs, setLocalPrefs] = useState(columnPrefs);

  const FIXED_COLUMN_OPTIONS = [
    { key: 'status', label: 'Status' },
    { key: 'doc_type', label: 'Doc Type' },
    { key: 'workflow', label: 'Workflow' },
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'results', label: 'Results' },
    { key: 'cost', label: 'Cost' },
    { key: 'time', label: 'Time' },
    { key: 'actions', label: 'Actions' },
  ];

  const toggleFixedColumn = (colKey) => {
    setLocalPrefs(prev => {
      const isVisible = prev.fixedColumns.includes(colKey);
      if (isVisible) {
        return { ...prev, fixedColumns: prev.fixedColumns.filter(c => c !== colKey) };
      } else {
        return { ...prev, fixedColumns: [...prev.fixedColumns, colKey] };
      }
    });
  };

  const toggleFieldColumn = (fieldKey) => {
    setLocalPrefs(prev => {
      const isVisible = prev.fieldColumns.includes(fieldKey);
      if (isVisible) {
        return { ...prev, fieldColumns: prev.fieldColumns.filter(f => f !== fieldKey) };
      } else {
        return { ...prev, fieldColumns: [...prev.fieldColumns, fieldKey] };
      }
    });
  };

  const handleSave = () => {
    onSave(localPrefs);
    onClose();
  };

  const handleReset = () => {
    const defaults = { fixedColumns: DEFAULT_COLUMNS, fieldColumns: [] };
    setLocalPrefs(defaults);
    onSave(defaults);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, pickerRef]);

  return (
    <div className="pm-column-picker" ref={pickerRef}>
      <div className="pm-column-picker-header">
        <h3>Configure Columns</h3>
        <button className="pm-column-picker-close" onClick={onClose}>&times;</button>
      </div>

      <div className="pm-column-picker-body">
        <div className="pm-column-section">
          <h4>Fixed Columns</h4>
          <div className="pm-column-list">
            {FIXED_COLUMN_OPTIONS.map(col => (
              <label key={col.key} className="pm-column-option">
                <input
                  type="checkbox"
                  checked={localPrefs.fixedColumns.includes(col.key)}
                  onChange={() => toggleFixedColumn(col.key)}
                />
                <span>{col.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pm-column-section">
          <h4>Extracted Field Columns</h4>
          <div className="pm-column-list">
            {fieldLibrary.length === 0 ? (
              <p className="pm-column-empty">No fields available</p>
            ) : (
              fieldLibrary.map(field => (
                <label key={field.field_key} className="pm-column-option">
                  <input
                    type="checkbox"
                    checked={localPrefs.fieldColumns.includes(field.field_key)}
                    onChange={() => toggleFieldColumn(field.field_key)}
                  />
                  <span>{field.label || field.field_key}</span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="pm-column-picker-footer">
        <button className="pm-column-btn pm-column-btn--reset" onClick={handleReset}>
          Reset to Default
        </button>
        <button className="pm-column-btn pm-column-btn--save" onClick={handleSave}>
          Apply
        </button>
      </div>
    </div>
  );
}

export default ProcessingMonitorPage;
